import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const gameTypes = [
    'S301', 'S301', 'D301',
    'S501', 'S501', 'D501',
    'SCricket', 'SCricket', 'DCricket'
];

const streakGameTypes = [
    'S301', 'D301',
    'S501', 'D501',
    'SCricket', 'DCricket'
];


export const DashboardPage = ({ userRole, loggedInUser, navigate }) => {
    // State for the selected season in the dropdown
    const [selectedSeason, setSelectedSeason] = useState("2024 Spring");
    const [scheduleSeasonNames, setScheduleSeasonNames] = useState([]);
    const [currentScheduleName, setCurrentScheduleName] = useState('');
    const [isDetailedModalOpen, setIsDetailedModalOpen] = useState(false);
    const [isSeasonModalOpen, setIsSeasonModalOpen] = useState(false);
    const [barsFilled, setBarsFilled] = useState(false);
    const [userStats, setUserStats] = useState({
        total180s: 0,
        highestScore: 0,
        highestIn: 0,
        highestOut: 0,
        totalTons: 0,
        totalC9: 0,
        totalHighIns: 0,

        totalC8: 0,
        totalC7: 0,
        totalC6: 0,
        streaks: {},
        highlightsPerWeek: 0,
        tonsPerWeek: 0,
        matchHistory: [],
        seasonWeeklyTrend: [],
        seasonGameStats: {
            S301: { w: 0, l: 0 }, D301: { w: 0, l: 0 },
            S501: { w: 0, l: 0 }, D501: { w: 0, l: 0 },
            SCricket: { w: 0, l: 0 }, DCricket: { w: 0, l: 0 }
        }
    });
    const [loading, setLoading] = useState(true);
    const [seasonTeam, setSeasonTeam] = useState(null);
    const [seasonGamePoints, setSeasonGamePoints] = useState(0);
    const [cumulativeStats, setCumulativeStats] = useState({
        seasonsPlayed: 0,
        distinctTeams: 0,
        totalTonsAll: 0,
        total180sAll: 0,
        highestOutAll: 0,
        highestInAll: 0,
    });

    useEffect(() => {
        if (isDetailedModalOpen || isSeasonModalOpen) {
            const timer = setTimeout(() => setBarsFilled(true), 100);
            return () => clearTimeout(timer);
        } else {
            setBarsFilled(false);
        }
    }, [isDetailedModalOpen, isSeasonModalOpen]);

    useEffect(() => {
        const fetchAndCalculateStats = async () => {
            if (!loggedInUser) return;
            setLoading(true);

            const schedulesSnapshot = await getDocs(collection(db, "schedules"));
            const allSchedules = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setScheduleSeasonNames(allSchedules.map(s => s.name).filter(Boolean));
            if (allSchedules.length > 0 && allSchedules[0]?.name) {
                setCurrentScheduleName(allSchedules[0].name);
            }
            const teamsSnapshot = await getDocs(collection(db, "teams"));
            const allTeams = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            let total180s = 0;
            let highestScore = 0;
            let highestIn = 0;
            let highestOut = 0;
            let totalTons = 0;
            let totalC9 = 0;
            let totalC8 = 0;
            let totalC7 = 0;
            let totalC6 = 0;

            const playerGames = [];
            const weeksPlayed = new Set();
            const matchHistory = [];
            const seasonWeeklyTrend = [];
            const seasonGameStats = {
                S301: { w: 0, l: 0 }, D301: { w: 0, l: 0 },
                S501: { w: 0, l: 0 }, D501: { w: 0, l: 0 },
                SCricket: { w: 0, l: 0 }, DCricket: { w: 0, l: 0 }
            };

            const relevantSchedules = selectedSeason ? allSchedules.filter(s => s.name === selectedSeason) : allSchedules;

            // Determine user's team for the selected season from match participation
            let derivedTeamId = null;
            for (const schedule of relevantSchedules) {
                for (const week of schedule.weeks) {
                    for (const game of week.games) {
                        const pa = game.results?.playerAssignments || {};
                        const entries = Object.entries(pa);
                        const involved = entries.find(([, pid]) => pid === loggedInUser.uid);
                        if (involved) {
                            // infer side
                            const isHome = involved[0].includes('-home-');
                            derivedTeamId = isHome ? game.homeTeamId : game.awayTeamId;
                            break;
                        }
                    }
                    if (derivedTeamId) break;
                }
                if (derivedTeamId) break;
            }
            setSeasonTeam(derivedTeamId ? allTeams.find(t => t.id === derivedTeamId) || null : null);

            // --- Team Game Points for Selected Season ---
            let teamGamePoints = 0;

            relevantSchedules.forEach(schedule => {
                schedule.weeks.forEach(week => {
                    week.games.forEach(game => {
                        const playerAssignments = game.results?.playerAssignments || {};
                        const isPlayerInMatch = Object.values(playerAssignments).includes(loggedInUser.uid);

                        if (isPlayerInMatch && game.results) {
                            // --- Streak Calculation Data Prep ---
                            Object.entries(playerAssignments).forEach(([key, playerId]) => {
                                if (playerId === loggedInUser.uid) {
                                    const [gameIdxStr, team] = key.split('-');
                                    const gameIdx = parseInt(gameIdxStr, 10);
                                    const gameType = gameTypes[gameIdx];
                                    const score = game.results.gameScores[gameIdx];
                                    const won = (team === 'home' && score.home > score.away) || (team === 'away' && score.away > score.home);
                                    playerGames.push({
                                        date: new Date(week.date),
                                        gameType: gameType,
                                        result: won ? 'W' : 'L'
                                    });

                                    // Track W/L by gameType for selected season
                                    if (seasonGameStats[gameType]) {
                                        if (won) seasonGameStats[gameType].w++;
                                        else seasonGameStats[gameType].l++;
                                    }
                                }
                            });

                            weeksPlayed.add(`${schedule.id}-${week.week}`); // Track unique weeks played

                            const playerHighlights = game.results.highlights?.[loggedInUser.uid];
                            if (playerHighlights) {
                                total180s += parseInt(playerHighlights['180s'] || 0, 10);
                                totalC9 += parseInt(playerHighlights.c9 || 0, 10);
                                totalC8 += parseInt(playerHighlights.c8 || 0, 10);
                                totalC7 += parseInt(playerHighlights.c7 || 0, 10);
                                totalC6 += parseInt(playerHighlights.c6 || 0, 10);

                                const tons = (playerHighlights.tons || '').split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                totalTons += tons.length;
                                const maxTon = tons.length > 0 ? Math.max(...tons) : 0;
                                if (maxTon > highestScore) highestScore = maxTon;

                                const highIns = (playerHighlights.on80 || '').split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                if (highIns.length > 0) highestIn = Math.max(highestIn, ...highIns);

                                const highOuts = (playerHighlights.highOut || '').split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                if (highOuts.length > 0) highestOut = Math.max(highestOut, ...highOuts);
                            }

                            const isHomeTeam = game.homeTeamId === loggedInUser.currentTeam?.id;
                            const opponentId = isHomeTeam ? game.awayTeamId : game.homeTeamId;
                            matchHistory.push({
                                id: `${schedule.id}-${week.week}-${game.homeTeamId}-${game.awayTeamId}`,
                                date: week.date,
                                opponent: allTeams.find(t => t.id === opponentId)?.name || 'N/A',
                                result: game.result || 'N/A',
                                isHomeTeam: isHomeTeam,
                                scheduleId: schedule.id,
                                weekIndex: schedule.weeks.findIndex(w => w.week === week.week),
                                gameIndex: week.games.findIndex(g => g.homeTeamId === game.homeTeamId && g.awayTeamId === game.awayTeamId)
                            });

                            // --- Build Season Weekly Trend ---
                            let matchW = 0, matchL = 0;
                            let homeMatchGamePoints = 0, awayMatchGamePoints = 0;
                            if (Array.isArray(game.results.gameScores)) {
                                game.results.gameScores.forEach((score) => {
                                    if (score.home > score.away) homeMatchGamePoints++;
                                    else if (score.away > score.home) awayMatchGamePoints++;
                                });
                            }
                            
                            const isHome = game.homeTeamId === derivedTeamId;
                            if (isHome) {
                                if (homeMatchGamePoints > awayMatchGamePoints) matchW = 1;
                                else if (awayMatchGamePoints > homeMatchGamePoints) matchL = 1;
                            } else {
                                if (awayMatchGamePoints > homeMatchGamePoints) matchW = 1;
                                else if (homeMatchGamePoints > awayMatchGamePoints) matchL = 1;
                            }
                            
                            let gameW = 0, gameL = 0;
                            let legW = 0, legL = 0;
                            Object.entries(playerAssignments).forEach(([key, playerId]) => {
                                if (playerId === loggedInUser.uid) {
                                    const [gameIdxStr, team] = key.split('-');
                                    if (Array.isArray(game.results.gameScores)) {
                                        const score = game.results.gameScores[parseInt(gameIdxStr, 10)];
                                        if (score) {
                                            const won = (team === 'home' && score.home > score.away) || (team === 'away' && score.away > score.home);
                                            if (won) gameW++; else gameL++;
                                            
                                            if (team === 'home') {
                                                legW += score.home || 0;
                                                legL += score.away || 0;
                                            } else {
                                                legW += score.away || 0;
                                                legL += score.home || 0;
                                            }
                                        }
                                    }
                                }
                            });
                            
                            const trendOpponentId = isHome ? game.awayTeamId : game.homeTeamId;
                            const opponentName = allTeams.find(t => t.id === trendOpponentId)?.name || 'N/A';
                            
                            seasonWeeklyTrend.push({
                                date: new Date(week.date),
                                weekName: week.week === 'Event' ? (game.eventName || 'Event') : `Week ${week.week}`,
                                opponentName,
                                matchW, matchL,
                                gameW, gameL,
                                legW, legL
                            });
                        }

                        // Accumulate team game points for the derived team within the selected season
                        if (game.results && derivedTeamId && (game.homeTeamId === derivedTeamId || game.awayTeamId === derivedTeamId)) {
                            let homeMatchGamePoints = 0;
                            let awayMatchGamePoints = 0;
                            game.results.gameScores.forEach((score) => {
                                if (score.home > score.away) homeMatchGamePoints++;
                                else if (score.away > score.home) awayMatchGamePoints++;
                            });
                            if (game.homeTeamId === derivedTeamId) teamGamePoints += homeMatchGamePoints;
                            else if (game.awayTeamId === derivedTeamId) teamGamePoints += awayMatchGamePoints;
                        }
                    });
                });
            });

            setSeasonGamePoints(teamGamePoints);

            // --- Cumulative Stats Across All Seasons ---
            const seasonsPlayedSet = new Set();
            const distinctTeamsSet = new Set();
            let totalTonsAll = 0;
            let total180sAll = 0;
            let highestOutAll = 0;
            let highestInAll = 0;
            let totalC9All = 0;
            let totalC8All = 0;
            let totalC7All = 0;
            let totalC6All = 0;
            const careerGameStats = {
                S301: { w: 0, l: 0 }, D301: { w: 0, l: 0 },
                S501: { w: 0, l: 0 }, D501: { w: 0, l: 0 },
                SCricket: { w: 0, l: 0 }, DCricket: { w: 0, l: 0 }
            };
            const teamsHistoryMap = new Map();
            const seasonTrendMap = new Map();
            const allPlayerGames = [];

            allSchedules.forEach(schedule => {
                let playedInThisSchedule = false;
                let seasonW = 0;
                let seasonL = 0;
                let seasonTeamId = null;
                schedule.weeks.forEach(week => {
                    week.games.forEach(game => {
                        const pa = game.results?.playerAssignments || {};
                        const entries = Object.entries(pa);
                        const involved = entries.find(([, pid]) => pid === loggedInUser.uid);
                        if (involved && game.results) {
                            playedInThisSchedule = true;
                            const isHome = involved[0].includes('-home-');
                            const teamId = isHome ? game.homeTeamId : game.awayTeamId;
                            if (teamId) {
                                distinctTeamsSet.add(teamId);
                                seasonTeamId = teamId;
                            }

                            // Track all games for career streak calculation
                            Object.entries(pa).forEach(([key, playerId]) => {
                                if (playerId === loggedInUser.uid) {
                                    const [gameIdxStr, team] = key.split('-');
                                    const gameIdx = parseInt(gameIdxStr, 10);
                                    const gameType = gameTypes[gameIdx];
                                    const score = game.results.gameScores[gameIdx];
                                    const won = (team === 'home' && score.home > score.away) || (team === 'away' && score.away > score.home);
                                    allPlayerGames.push({
                                        date: new Date(week.date),
                                        gameType: gameType,
                                        result: won ? 'W' : 'L'
                                    });

                                    // Track W/L by gameType for career
                                    if (careerGameStats[gameType]) {
                                        if (won) { careerGameStats[gameType].w++; seasonW++; }
                                        else { careerGameStats[gameType].l++; seasonL++; }
                                    }
                                }
                            });

                            const h = game.results.highlights?.[loggedInUser.uid];
                            if (h) {
                                const tons = (h.tons || '').split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                totalTonsAll += tons.length;
                                const highInsVals = ((h.on80 || h.highIn) || '')
                                    .split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                if (highInsVals.length > 0) highestInAll = Math.max(highestInAll, ...highInsVals);
                                const highOutVals = (h.highOut || '')
                                    .split(',').map(t => parseInt(t.trim(), 10)).filter(n => !isNaN(n));
                                if (highOutVals.length > 0) highestOutAll = Math.max(highestOutAll, ...highOutVals);
                                const n180 = parseInt(h['180s'] || 0, 10);
                                if (!isNaN(n180)) total180sAll += n180;
                                
                                const c9 = parseInt(h.c9 || h.C9 || 0, 10); if (!isNaN(c9)) totalC9All += c9;
                                const c8 = parseInt(h.c8 || h.C8 || 0, 10); if (!isNaN(c8)) totalC8All += c8;
                                const c7 = parseInt(h.c7 || h.C7 || 0, 10); if (!isNaN(c7)) totalC7All += c7;
                                const c6 = parseInt(h.c6 || h.C6 || 0, 10); if (!isNaN(c6)) totalC6All += c6;
                            }
                        }
                    });
                });
                if (playedInThisSchedule) {
                    seasonsPlayedSet.add(schedule.id);
                    const tName = allTeams.find(t => t.id === seasonTeamId)?.name || 'Unknown Team';
                    const sName = schedule.name || `Season ${schedule.id}`;
                    teamsHistoryMap.set(schedule.id, { seasonName: sName, teamName: tName });
                    seasonTrendMap.set(schedule.id, { seasonName: sName, w: seasonW, l: seasonL });
                }
            });

            // --- Calculate Streaks ---
            const streaks = {};
            playerGames.sort((a, b) => a.date - b.date); // Sort games chronologically
            allPlayerGames.sort((a, b) => a.date - b.date);

            const calculateStreak = (gamesOfType) => {
                if (gamesOfType.length === 0) {
                    return { current: { type: '-', count: 0 }, longest: { type: '-', count: 0 } };
                }
                let currentType = null;
                let currentCount = 0;
                let maxType = '-';
                let maxCount = 0;

                gamesOfType.forEach(g => {
                    if (g.result === currentType) {
                        currentCount++;
                    } else {
                        currentType = g.result;
                        currentCount = 1;
                    }

                    if (currentCount > maxCount) {
                        maxCount = currentCount;
                        maxType = currentType;
                    } else if (currentCount === maxCount && currentType === 'W' && maxType === 'L') {
                        maxType = 'W';
                    }
                });

                return { current: { type: currentType, count: currentCount }, longest: { type: maxType, count: maxCount } };
            };

            streakGameTypes.forEach(gameType => {
                const seasonGamesOfType = playerGames.filter(g => g.gameType === gameType);
                const careerGamesOfType = allPlayerGames.filter(g => g.gameType === gameType);

                const seasonStreak = calculateStreak(seasonGamesOfType);
                const careerStreak = calculateStreak(careerGamesOfType);

                streaks[gameType] = {
                    current: seasonStreak.current,
                    season: seasonStreak.longest,
                    career: careerStreak.longest
                };
            });

            seasonWeeklyTrend.sort((a, b) => a.date - b.date);
            const sortedHistory = matchHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
            const numWeeksPlayed = weeksPlayed.size > 0 ? weeksPlayed.size : 1; // Avoid division by zero
            const totalHighlights = total180s + totalTons + totalC9 + totalC8 + totalC7 + totalC6;
            const highlightsPerWeek = totalHighlights / numWeeksPlayed;
            const tonsPerWeek = totalTons / numWeeksPlayed;

            setUserStats({
                total180s,
                highestScore,
                highestIn,
                highestOut,
                totalTons,
                totalC9,
                totalC8,
                totalC7,
                totalC6,
                streaks,
                highlightsPerWeek: highlightsPerWeek.toFixed(2),
                tonsPerWeek: tonsPerWeek.toFixed(2),
                matchHistory: sortedHistory,
                seasonGameStats,
                seasonWeeklyTrend,
            });

            setCumulativeStats({
                seasonsPlayed: seasonsPlayedSet.size,
                distinctTeams: distinctTeamsSet.size,
                totalTonsAll,
                total180sAll,
                highestOutAll,
                highestInAll,
                totalC9All,
                totalC8All,
                totalC7All,
                totalC6All,
                careerGameStats,
                teamsHistory: Array.from(teamsHistoryMap.values()),
                seasonTrend: Array.from(seasonTrendMap.values())
            });

            setLoading(false);
        };

        fetchAndCalculateStats();
    }, [loggedInUser, selectedSeason]);

    // Compute available seasons from user pointTotals; ensure selection always stays valid
    const availableSeasons = useMemo(() => {
        const keys = loggedInUser?.pointTotals ? Object.keys(loggedInUser.pointTotals) : [];
        const names = scheduleSeasonNames || [];
        const set = new Set([...keys, ...names]);
        return Array.from(set).filter(Boolean).sort((a, b) => b.localeCompare(a));
    }, [loggedInUser?.pointTotals, scheduleSeasonNames]);

    useEffect(() => {
        if (availableSeasons.length === 0) {
            if (selectedSeason !== '') setSelectedSeason('');
            return;
        }
        if (!availableSeasons.includes(selectedSeason)) {
            if (currentScheduleName && availableSeasons.includes(currentScheduleName)) {
                setSelectedSeason(currentScheduleName);
            } else {
                setSelectedSeason(availableSeasons[0]);
            }
        }
    }, [availableSeasons, selectedSeason, currentScheduleName]);

    // Function to handle season change
    const handleSeasonChange = (event) => {
        const value = event.target.value;
        if (availableSeasons.includes(value)) {
            setSelectedSeason(value);
        }
    };

    if (userRole !== 'member' && userRole !== 'admin') {
        return <p className="text-xl text-danger text-center">Please log in as a member or admin to access the dashboard.</p>;
    }

    if (loading || !loggedInUser) {
        return <p className="text-xl text-muted text-center">Loading user data...</p>;
    }

    // Determine current point total for the selected season
    const currentPointTotal = seasonGamePoints || 0;
    const displayedSeason = selectedSeason || 'N/A';

    return (
        <div className="space-y-6">
            <h2 className="page-title heading-primary-1">Member Dashboard</h2>

            {/* Mobile: Career Profile Card */}
            <div className="block sm:hidden mb-4 -mx-3 sm:mx-0">
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col gap-3 shadow-md transition-all duration-200 ease-in-out hover:shadow-xl">
                    <h3 className="text-2xl font-bold heading-primary-1 mb-3">Career Profile</h3>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted">Name</span>
                        <span className="text-strong font-bold text-right">{loggedInUser.nickname ? `${loggedInUser.nickname} (${loggedInUser.firstName} ${loggedInUser.lastName})` : `${loggedInUser.firstName} ${loggedInUser.lastName}`}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted">Seasons Played</span>
                        <span className="text-strong font-bold">{cumulativeStats.seasonsPlayed}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted">Distinct Teams</span>
                        <span className="text-strong font-bold">{cumulativeStats.distinctTeams}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="surface-low p-2 rounded-md text-center border border-low">
                            <div className="text-xs text-muted">Ton+</div>
                            <div className="font-bold subheading-primary-1 text-lg">{cumulativeStats.totalTonsAll}</div>
                        </div>
                        <div className="surface-low p-2 rounded-md text-center border border-low">
                            <div className="text-xs text-muted">180s</div>
                            <div className="font-bold subheading-primary-1 text-lg">{cumulativeStats.total180sAll}</div>
                        </div>
                        <div className="surface-low p-2 rounded-md text-center border border-low">
                            <div className="text-xs text-muted">High In</div>
                            <div className="font-bold subheading-primary-1 text-lg">{cumulativeStats.highestInAll > 0 ? cumulativeStats.highestInAll : '-'}</div>
                        </div>
                        <div className="surface-low p-2 rounded-md text-center border border-low">
                            <div className="text-xs text-muted">High Out</div>
                            <div className="font-bold subheading-primary-1 text-lg">{cumulativeStats.highestOutAll > 0 ? cumulativeStats.highestOutAll : '-'}</div>
                        </div>
                    </div>
                    <button onClick={() => setIsDetailedModalOpen(true)} className="btn btn-primary w-full text-sm mt-1">View Career Stats</button>
                </div>
            </div>

            {/* Mobile: Season Context Card */}
            <div className="block sm:hidden mb-5 -mx-3 sm:mx-0">
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col gap-3 shadow-md transition-all duration-200 ease-in-out hover:shadow-xl">
                    <div className="flex items-center justify-between border-b border-low pb-2">
                        <label htmlFor="season-select-mobile" className="text-sm font-bold text-muted uppercase tracking-wider shrink-0">Season</label>
                        <select
                            id="season-select-mobile"
                            value={selectedSeason}
                            onChange={handleSeasonChange}
                            className="select-field py-1 px-2 font-semibold text-sm w-auto ml-3 truncate"
                        >
                            {availableSeasons.length > 0 ? (
                                availableSeasons.map(season => (
                                    <option key={season} value={season}>{season}</option>
                                ))
                            ) : (
                                <option value="" disabled>No seasons available</option>
                            )}
                        </select>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted shrink-0">Team</span>
                        <span className="subheading-primary-1 font-bold text-right flex-1 min-w-0 ml-3 whitespace-normal break-words">{seasonTeam?.name || loggedInUser.currentTeam?.name || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-muted">Division</span>
                        <span className="text-strong font-bold">{loggedInUser.division || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-muted">Game Points ({displayedSeason})</span>
                        <span className="subheading-primary-1 font-bold text-lg">{currentPointTotal}</span>
                    </div>
                    <button onClick={() => setIsSeasonModalOpen(true)} className="btn btn-primary w-full text-sm mt-3">View Season Stats</button>
                </div>
            </div>

            {/* Desktop/Tablet: Top Tiles for User Info and Season Select */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-5">
                {/* Name Tile */}
                <div className="card rounded-lg p-2 sm:p-4 text-left sm:text-center flex flex-col justify-between items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <p className="label">Name</p>
                    <p className="text-xl font-bold text-strong">{loggedInUser.nickname ? `${loggedInUser.nickname} (${loggedInUser.firstName} ${loggedInUser.lastName})` : `${loggedInUser.firstName} ${loggedInUser.lastName}`}</p></div>
                {/* Team Tile */}
                <div className="card rounded-lg p-2 sm:p-4 text-left sm:text-center flex flex-col justify-between items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <p className="label">Team</p>
                    <p className="text-xl font-bold subheading-primary-1 whitespace-normal break-words">{seasonTeam?.name || loggedInUser.currentTeam?.name || 'N/A'}</p>
                </div>

                {/* Division Tile */}
                <div className="card rounded-lg p-2 sm:p-4 text-left sm:text-center flex flex-col justify-between items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <p className="label">Division</p>
                    <p className="text-xl font-bold text-strong">{loggedInUser.division || 'N/A'}</p>
                </div>

                {/* Game Points Tile */}
                <div className="card rounded-lg p-2 sm:p-4 text-left sm:text-center flex flex-col justify-between items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <p className="text-sm font-semibold text-muted">Game Points ({displayedSeason})</p>
                    <p className="text-xl font-bold subheading-primary-1">{currentPointTotal}</p>
                </div>

                {/* Season Dropdown Tile */}
                <div className="card rounded-lg p-2 sm:p-4 text-left sm:text-center flex flex-col justify-between items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <label htmlFor="season-select" className="field-label mb-2">Select Season</label>
                    <select
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        className="select-field mt-1 block w-full py-1 px-2"
                    >
                        {availableSeasons.length > 0 ? (
                            availableSeasons.map(season => (
                                <option key={season} value={season}>{season}</option>
                            ))
                        ) : (
                            <option value="" disabled>No seasons available</option>
                        )}
                    </select>
                </div>
            </div>

            {/* Main Dashboard Content Area - mobile-first tweaks (widen on mobile) */}
            <div className="-mx-3 sm:mx-0 grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Cumulative Statistics Tile */}
                <div className="hidden sm:flex card rounded-lg p-3 sm:p-5 flex-col justify-between transition-all duration-200 ease-in-out hover:shadow-xl">
                    <h3 className="text-2xl font-bold heading-primary-1 mb-3">Your Statistics</h3>
                    <div className="rounded-lg p-2 sm:p-3 space-y-1">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted">Seasons Played</span>
                            <span className="text-lg font-bold subheading-primary-1">{cumulativeStats.seasonsPlayed}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted">Distinct Teams</span>
                            <span className="text-lg font-bold subheading-primary-1">{cumulativeStats.distinctTeams}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted">Ton+</span>
                                <span className="text-xl font-bold subheading-primary-1">{cumulativeStats.totalTonsAll}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted">180s</span>
                                <span className="text-xl font-bold subheading-primary-1">{cumulativeStats.total180sAll}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted">Highest Out</span>
                                <span className="text-xl font-bold subheading-primary-1">{cumulativeStats.highestOutAll > 0 ? cumulativeStats.highestOutAll : '-'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted">Highest On</span>
                                <span className="text-xl font-bold subheading-primary-1">{cumulativeStats.highestInAll > 0 ? cumulativeStats.highestInAll : '-'}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={() => setIsDetailedModalOpen(true)} className="btn btn-primary mt-2 w-full sm:w-auto self-start">View Career Stats</button>
                </div>

                {/* Highlights Tile */}
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col transition-all duration-200 ease-in-out hover:shadow-xl">
                    <h3 className="text-2xl font-bold heading-primary-3 mb-3">Season Highlights</h3>
                    <div className="rounded-lg p-2 sm:p-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">Tons</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.totalTons}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">High Ton+</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.highestScore > 0 ? userStats.highestScore : '-'}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">180s</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.total180s}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">High In</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.highestIn > 0 ? userStats.highestIn : '-'}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">High Out</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.highestOut > 0 ? userStats.highestOut : '-'}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">6-Hits</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.totalC6}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">7-Hits</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.totalC7}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">8-Hits</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.totalC8}</p>
                            </div>
                            <div className="sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">9-Hits</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.totalC9}</p>
                            </div>
                            <div className="col-span-2 sm:col-span-3 sm:text-center flex items-center justify-between sm:block">
                                <p className="text-sm text-muted">Highlights / Week</p>
                                <p className="text-xl font-bold subheading-primary-1 sm:mt-1">{userStats.highlightsPerWeek}</p>
                            </div>
                        </div>
                    </div>
                    <button onClick={() => setIsSeasonModalOpen(true)} className="btn btn-primary mt-4 w-full sm:w-auto self-start">View Season Stats</button>
                </div>

                {/* Streak Tile */}
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col items-start sm:items-center transition-all duration-200 ease-in-out hover:shadow-xl">
                    <h3 className="text-2xl font-bold heading-secondary-1 mb-3">Game Streaks</h3>
                    <div className="w-full space-y-2">
                        <div className="flex items-center justify-between text-sm font-medium text-muted mb-1 px-1">
                            <span className="flex-1">Game</span>
                            <span className="w-[3.5rem] sm:w-16 text-center">Current</span>
                            <span className="w-[3.5rem] sm:w-16 text-center">Season</span>
                            <span className="w-[3.5rem] sm:w-16 text-center">Career</span>
                        </div>
                        {Object.entries(userStats.streaks).map(([gameType, streakData]) => (
                            <div key={gameType} className="flex items-center justify-between px-1">
                                <span className="font-semibold text-strong flex-1">{gameType}</span>
                                <div className="w-[3.5rem] sm:w-16 text-center">
                                    {streakData.current?.count > 0 ? (
                                        <span className={`tag ${streakData.current.type === 'W' ? 'tag-success' : streakData.current.type === 'L' ? 'tag-danger' : 'tag-neutral'}`}>{`${streakData.current.type}${streakData.current.count}`}</span>
                                    ) : (
                                        <span className="tag tag-neutral">-</span>
                                    )}
                                </div>
                                <div className="w-[3.5rem] sm:w-16 text-center">
                                    {streakData.season?.count > 0 ? (
                                        <span className={`tag ${streakData.season.type === 'W' ? 'tag-success' : streakData.season.type === 'L' ? 'tag-danger' : 'tag-neutral'}`}>{`${streakData.season.type}${streakData.season.count}`}</span>
                                    ) : (
                                        <span className="tag tag-neutral">-</span>
                                    )}
                                </div>
                                <div className="w-[3.5rem] sm:w-16 text-center">
                                    {streakData.career?.count > 0 ? (
                                        <span className={`tag ${streakData.career.type === 'W' ? 'tag-success' : streakData.career.type === 'L' ? 'tag-danger' : 'tag-neutral'}`}>{`${streakData.career.type}${streakData.career.count}`}</span>
                                    ) : (
                                        <span className="tag tag-neutral">-</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Match History Tile */}
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col justify-between transition-all duration-200 ease-in-out hover:shadow-xl">
                    <h3 className="text-2xl font-bold heading-secondary-3 mb-3">Match History</h3>
                    <div className="-mx-3 sm:mx-0 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {userStats.matchHistory.slice(0, 6).map(match => {
                            const scores = match.result.split('-');
                            const homeScore = scores[0]?.trim();
                            const awayScore = scores[1]?.trim();
                            const displayResult = match.isHomeTeam ? `${homeScore} - ${awayScore}` : `${awayScore} - ${homeScore}`;
                            const homeNum = parseInt(homeScore, 10);
                            const awayNum = parseInt(awayScore, 10);
                            const validScores = !isNaN(homeNum) && !isNaN(awayNum);
                            const isWin = validScores ? (match.isHomeTeam ? homeNum > awayNum : awayNum > homeNum) : null;
                            const formattedDate = (() => {
                                try {
                                    const d = new Date(match.date);
                                    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                } catch (e) {
                                    return match.date;
                                }
                            })();
                            const locLabel = match.isHomeTeam ? 'Home' : 'Away';
                            const locLabelShort = match.isHomeTeam ? 'H' : 'A';
                            return (
                                <div key={match.id} className="card rounded-lg p-3 sm:p-4 flex flex-col justify-between">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm text-muted">{formattedDate}</p>
                                        <div className="flex items-center gap-1">
                                            <span className={`tag ${isWin === null ? 'tag-neutral' : isWin ? 'tag-success' : 'tag-danger'}`} title={locLabel}>{`${locLabelShort} ${displayResult}`}</span>
                                        </div>
                                    </div>
                                    <p className="mt-1 text-lg font-semibold text-strong text-left">{match.opponent}</p>
                                    <button aria-label="View match details" onClick={() => navigate('Match Results Summary', { scheduleId: match.scheduleId, weekIndex: match.weekIndex, gameIndex: match.gameIndex, from: 'Dashboard' })} className="btn btn-primary mt-2 w-full sm:w-auto sm:self-end">View</button>
                                </div>
                            );
                        })}
                    </div>
                    <button onClick={() => navigate('Schedule')} className="btn btn-primary mt-3 w-full sm:w-auto self-start">View Full Schedule</button>
                </div>

                {/* Team Overview Tile (linking to My Team) */}
                <div className="card rounded-lg p-3 sm:p-5 flex flex-col justify-between transition-all duration-200 ease-in-out hover:shadow-xl md:col-span-2">
                    <h3 className="text-2xl font-bold heading-primary-2 mb-2">Team Overview</h3>
                    <p className="text-lg text-strong mb-2 text-left">
                        You are a proud member of <strong className="font-semibold subheading-primary-1 inline-block align-bottom whitespace-normal break-words">{loggedInUser.currentTeam?.name || 'N/A'}</strong>.
                        Check out your team's upcoming matches, standings, and roster details.
                    </p>
                    <button onClick={() => navigate('My Team')} className="btn btn-primary mt-auto w-full sm:w-auto self-start">Go to My Team Page</button>
                </div>
            </div>

            {/* Detailed Career Stats Modal */}
            {isDetailedModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsDetailedModalOpen(false)}>
                    <div className="card rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden border border-primary-two" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-low surface-low">
                            <h3 className="text-2xl font-extrabold heading-primary-1">Career Detailed Statistics</h3>
                            <button onClick={() => setIsDetailedModalOpen(false)} className="text-muted hover:text-strong text-3xl leading-none">&times;</button>
                        </div>
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-8">
                            
                            {/* Highlights Grid */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Career Highlights</h4>
                                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center text-sm">
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">Ton+</span><span className="font-bold text-strong text-lg">{cumulativeStats.totalTonsAll}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">180s</span><span className="font-bold text-strong text-lg">{cumulativeStats.total180sAll}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">High In</span><span className="font-bold text-strong text-lg">{cumulativeStats.highestInAll || '-'}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">High Out</span><span className="font-bold text-strong text-lg">{cumulativeStats.highestOutAll || '-'}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">9-Hits</span><span className="font-bold text-strong text-lg">{cumulativeStats.totalC9All}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">8-Hits</span><span className="font-bold text-strong text-lg">{cumulativeStats.totalC8All}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">7-Hits</span><span className="font-bold text-strong text-lg">{cumulativeStats.totalC7All}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">6-Hits</span><span className="font-bold text-strong text-lg">{cumulativeStats.totalC6All}</span></div>
                                </div>
                            </section>

                            {/* Win % By Game Type */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Win % by Game Type</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    {['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'].map(gType => {
                                        const stats = cumulativeStats.careerGameStats[gType] || { w: 0, l: 0 };
                                        const total = stats.w + stats.l;
                                        const pct = total > 0 ? Math.round((stats.w / total) * 100) : 0;

                                        return (
                                            <div key={gType} className="surface-low p-3 rounded-lg border border-low flex flex-col hover:shadow-md transition-shadow">
                                                <div className="flex justify-between font-semibold text-sm mb-1.5">
                                                    <span className="text-strong">{gType}</span>
                                                    <span className="text-muted">{stats.w}W - {stats.l}L <span className="text-strong">({total > 0 ? `${pct}%` : 'N/A'})</span></span>
                                                </div>
                                                <div className="w-full bg-neutral-soft rounded-full h-4 relative overflow-hidden flex items-center px-0.5 shadow-inner border border-low">
                                                    {/* Glass reflection overlays for the "tube" effect */}
                                                    <div className="absolute inset-x-0 top-0 h-1.5 bg-white/20 rounded-t-full pointer-events-none z-10"></div>
                                                    <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10 rounded-b-full pointer-events-none z-10"></div>
                                                    {/* The inner "power" line */}
                                                    <div
                                                        className="h-2 rounded-full transition-all duration-1000 ease-out relative z-0"
                                                        style={{
                                                                width: barsFilled && total > 0 ? `${pct}%` : '0%',
                                                                backgroundColor: 'var(--primary-three)',
                                                            boxShadow: '0 0 6px var(--accent-bg)'
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>

                            {/* Season-by-Season Trend */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Season Win/Loss Trend</h4>
                                {cumulativeStats.seasonTrend.length > 0 ? (
                                    <div className="space-y-3">
                                        {cumulativeStats.seasonTrend.map((st, i) => {
                                            const total = st.w + st.l;
                                            const wPct = total > 0 ? (st.w / total) * 100 : 0;
                                            const lPct = total > 0 ? (st.l / total) * 100 : 0;
                                            return (
                                                <div key={i} className="flex items-center gap-3">
                                                    <div className="w-1/3 sm:w-1/4 text-xs sm:text-sm font-semibold truncate text-right text-muted">{st.seasonName}</div>
                                                    <div className="flex-1 flex items-center gap-2">
                                                        <div className="flex-1 bg-neutral-soft rounded-full h-4 relative overflow-hidden flex items-center px-0.5 shadow-inner border border-low">
                                                            <div className="absolute inset-x-0 top-0 h-1.5 bg-white/20 rounded-t-full pointer-events-none z-10"></div>
                                                            <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10 rounded-b-full pointer-events-none z-10"></div>
                                                            <div className="flex w-full h-2 rounded-full overflow-hidden relative z-0">
                                                                {total > 0 ? (
                                                                    <>
                                                                        <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${wPct}%` : '0%', backgroundColor: 'var(--success-bg)', boxShadow: '0 0 6px var(--success-bg)' }}></div>
                                                                        <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${lPct}%` : '0%', backgroundColor: 'var(--danger-bg)', boxShadow: '0 0 6px var(--danger-bg)' }}></div>
                                                                    </>
                                                                ) : (
                                                                    <div className="bg-neutral-soft h-full w-full"></div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="w-12 text-right text-xs font-bold text-strong whitespace-nowrap">{st.w}W - {st.l}L</div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-muted italic">No historical season data available.</p>
                                )}
                            </section>

                            {/* Teams History */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Teams History</h4>
                                {cumulativeStats.teamsHistory.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {cumulativeStats.teamsHistory.map((th, i) => (
                                            <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-low surface-low shadow-sm hover:shadow-md transition-shadow">
                                                <span className="font-semibold text-strong text-sm">{th.teamName}</span>
                                                <span className="text-xs text-muted border-l border-low pl-1.5">{th.seasonName}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted italic">No team history available.</p>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
            )}

            {/* Detailed Season Stats Modal */}
            {isSeasonModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsSeasonModalOpen(false)}>
                    <div className="card rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden border border-primary-two" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-low surface-low">
                            <h3 className="text-2xl font-extrabold heading-primary-1">Season Detailed Statistics ({displayedSeason})</h3>
                            <button onClick={() => setIsSeasonModalOpen(false)} className="text-muted hover:text-strong text-3xl leading-none">&times;</button>
                        </div>
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-8">
                            
                            {/* Highlights Grid */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Season Highlights</h4>
                                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center text-sm">
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">Ton+</span><span className="font-bold text-strong text-lg">{userStats.totalTons}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">180s</span><span className="font-bold text-strong text-lg">{userStats.total180s}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">High In</span><span className="font-bold text-strong text-lg">{userStats.highestIn || '-'}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">High Out</span><span className="font-bold text-strong text-lg">{userStats.highestOut || '-'}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">9-Hits</span><span className="font-bold text-strong text-lg">{userStats.totalC9}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">8-Hits</span><span className="font-bold text-strong text-lg">{userStats.totalC8}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">7-Hits</span><span className="font-bold text-strong text-lg">{userStats.totalC7}</span></div>
                                    <div className="surface-low border border-low rounded-lg p-2 flex flex-col hover:shadow-md transition-shadow"><span className="text-muted text-xs">6-Hits</span><span className="font-bold text-strong text-lg">{userStats.totalC6}</span></div>
                                </div>
                            </section>

                            {/* Win % By Game Type */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Win % by Game Type</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    {['S301', 'D301', 'S501', 'D501', 'SCricket', 'DCricket'].map(gType => {
                                        const stats = userStats.seasonGameStats?.[gType] || { w: 0, l: 0 };
                                        const total = stats.w + stats.l;
                                        const pct = total > 0 ? Math.round((stats.w / total) * 100) : 0;

                                        return (
                                            <div key={gType} className="surface-low p-3 rounded-lg border border-low flex flex-col hover:shadow-md transition-shadow">
                                                <div className="flex justify-between font-semibold text-sm mb-1.5">
                                                    <span className="text-strong">{gType}</span>
                                                    <span className="text-muted">{stats.w}W - {stats.l}L <span className="text-strong">({total > 0 ? `${pct}%` : 'N/A'})</span></span>
                                                </div>
                                                <div className="w-full bg-neutral-soft rounded-full h-4 relative overflow-hidden flex items-center px-0.5 shadow-inner border border-low">
                                                    {/* Glass reflection overlays for the "tube" effect */}
                                                    <div className="absolute inset-x-0 top-0 h-1.5 bg-white/20 rounded-t-full pointer-events-none z-10"></div>
                                                    <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10 rounded-b-full pointer-events-none z-10"></div>
                                                    {/* The inner "power" line */}
                                                    <div
                                                        className="h-2 rounded-full transition-all duration-1000 ease-out relative z-0"
                                                        style={{
                                                            width: barsFilled && total > 0 ? `${pct}%` : '0%',
                                                            backgroundColor: 'var(--accent-bg)',
                                                            boxShadow: '0 0 6px var(--accent-bg)'
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>

                            {/* Weekly Win/Loss Trend */}
                            <section>
                                <h4 className="text-xl font-bold subheading-primary-1 mb-4 flex items-center gap-2">Weekly Win/Loss Trend</h4>
                                {userStats.seasonWeeklyTrend && userStats.seasonWeeklyTrend.length > 0 ? (
                                    <div className="space-y-3">
                                        {userStats.seasonWeeklyTrend.map((wt, i) => {
                                            const gameTotal = wt.gameW + wt.gameL;
                                            const gameWPct = gameTotal > 0 ? (wt.gameW / gameTotal) * 100 : 0;
                                            const gameLPct = gameTotal > 0 ? (wt.gameL / gameTotal) * 100 : 0;

                                            const legTotal = (wt.legW || 0) + (wt.legL || 0);
                                            const legWPct = legTotal > 0 ? (wt.legW / legTotal) * 100 : 0;
                                            const legLPct = legTotal > 0 ? (wt.legL / legTotal) * 100 : 0;

                                            return (
                                                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-3 surface-low border border-low rounded-lg p-3 hover:shadow-md transition-shadow">
                                                    <div className="w-full sm:w-1/3 md:w-1/4 flex flex-col sm:items-end gap-0.5">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`tag tag-solid ${wt.matchW > 0 ? 'tag-success' : (wt.matchL > 0 ? 'tag-danger' : 'tag-neutral')} px-1.5 py-0 min-w-[1.5rem] justify-center shadow-sm`}>
                                                                {wt.matchW > 0 ? 'W' : (wt.matchL > 0 ? 'L' : '-')}
                                                            </span>
                                                            <span className="text-sm font-semibold text-strong truncate">{wt.weekName}</span>
                                                        </div>
                                                        <span className="text-muted font-normal text-xs truncate">vs {wt.opponentName}</span>
                                                    </div>
                                                    <div className="flex-1 flex flex-col gap-1.5">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-12 text-[10px] text-muted text-right uppercase tracking-wider font-bold">Games</div>
                                                            <div className="flex-1 bg-neutral-soft rounded-full h-4 relative overflow-hidden flex items-center px-0.5 shadow-inner border border-low">
                                                                <div className="absolute inset-x-0 top-0 h-1.5 bg-white/20 rounded-t-full pointer-events-none z-10"></div>
                                                                <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10 pointer-events-none z-10"></div>
                                                                <div className="flex w-full h-2 rounded-full overflow-hidden relative z-0">
                                                                    {gameTotal > 0 ? (
                                                                        <>
                                                                            <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${gameWPct}%` : '0%', backgroundColor: 'var(--success-bg)', boxShadow: '0 0 6px var(--success-bg)' }}></div>
                                                                            <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${gameLPct}%` : '0%', backgroundColor: 'var(--danger-bg)', boxShadow: '0 0 6px var(--danger-bg)' }}></div>
                                                                        </>
                                                                    ) : (
                                                                        <div className="bg-neutral-soft h-full w-full"></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="w-12 text-right text-[10px] font-bold text-strong whitespace-nowrap">{wt.gameW}W - {wt.gameL}L</div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-12 text-[10px] text-muted text-right uppercase tracking-wider font-bold">Legs</div>
                                                            <div className="flex-1 bg-neutral-soft rounded-full h-4 relative overflow-hidden flex items-center px-0.5 shadow-inner border border-low">
                                                                <div className="absolute inset-x-0 top-0 h-1.5 bg-white/20 rounded-t-full pointer-events-none z-10"></div>
                                                                <div className="absolute inset-x-0 bottom-0 h-1 bg-black/10 pointer-events-none z-10"></div>
                                                                <div className="flex w-full h-2 rounded-full overflow-hidden relative z-0">
                                                                    {legTotal > 0 ? (
                                                                        <>
                                                                            <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${legWPct}%` : '0%', backgroundColor: 'var(--success-bg)', boxShadow: '0 0 6px var(--success-bg)' }}></div>
                                                                            <div className="transition-all duration-1000 ease-out h-full" style={{ width: barsFilled ? `${legLPct}%` : '0%', backgroundColor: 'var(--danger-bg)', boxShadow: '0 0 6px var(--danger-bg)' }}></div>
                                                                        </>
                                                                    ) : (
                                                                        <div className="bg-neutral-soft h-full w-full"></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="w-12 text-right text-[10px] font-bold text-strong whitespace-nowrap">{wt.legW}W - {wt.legL}L</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-muted italic">No weekly trend data available for this season.</p>
                                )}
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};